import { Button, Header } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import React, { FunctionComponent, useCallback, useState } from 'react'
import { userHasPermission } from '@vend/utilities'
import {
  useQuickKeysManager,
  useUserManager,
  useUserSwitchingService,
  useWebRegisterStatusManager,
} from '../../ng-compat/hooks'
import { QuickKeysManager } from '../../managers/quick-keys-manager'
import { $state } from '../../ng-compat/imports'
import { STATES } from '../../../../lib/routes'
import { PERMISSIONS } from '../../constants'
import { useAppSelector } from '../../state/hooks'
import {
  selectActiveRegisterId,
  selectTrainingModeEnabled,
} from '../../state/slices/posState/posStateSlice'

const FauxQuickKeys: FunctionComponent = () => (
  <div className="vd-quick-keys wr-faux-quick-keys vd-mb4">
    <div className="vd-quick-key-grid">
      <div className="vd-quick-key-page">
        <div className="vd-quick-key-page-column">
          <div className="vd-quick-key vd-quick-key--yellow vd-quick-key--quick-key">
            <div className="vd-quick-key-inner">
              <span className="vd-quick-key-label">
                <T _str="Giftcard" _context="Fake quick key" />
              </span>
            </div>
          </div>
        </div>
        <div className="vd-quick-key-page-column">
          <div className="vd-quick-key vd-quick-key--purple vd-quick-key--quick-key">
            <div className="vd-quick-key-inner">
              <span className="vd-quick-key-label">
                <T _str="Bracelet" _context="Fake quick key" />
              </span>
            </div>
          </div>
        </div>
        <div className="vd-quick-key-page-column">
          <div className="vd-quick-key vd-quick-key--orange vd-quick-key--quick-key">
            <div className="vd-quick-key-inner">
              <span className="vd-quick-key-label">
                <T _str="Sunnies" _context="Fake quick key" />
              </span>
            </div>
          </div>
        </div>
        <div className="vd-quick-key-page-column">
          <div className="vd-quick-key vd-quick-key--red vd-quick-key--quick-key">
            <div className="vd-quick-key-inner">
              <span className="vd-quick-key-label">
                <T _str="Red Beanie" _context="Fake quick key" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
)

const getHasQuickKeyLayouts = (quickKeysManager: QuickKeysManager) => {
  return quickKeysManager
    .getAllQuickKeyLayouts()
    .then(layouts => Boolean(layouts.length))
}

export const NoQuickKeys: FunctionComponent = () => {
  const [setupInProgress, setSetupInProgress] = useState(false)
  const quickKeysManager = useQuickKeysManager()
  const userSwitchingService = useUserSwitchingService()
  const trainingModeEnabled = useAppSelector(selectTrainingModeEnabled)
  const selectedRegister = useAppSelector(selectActiveRegisterId)
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const userManager = useUserManager()
  const user = userManager.getLoggedInUser()

  const hasPermission = userHasPermission(user, PERMISSIONS.QUICK_KEYS_MANAGE)

  const switchUser = useCallback(() => {
    userSwitchingService.selectUser().catch(() => {})
  }, [userSwitchingService])

  const setUpQuickKeys = useCallback(() => {
    setSetupInProgress(true)
    getHasQuickKeyLayouts(quickKeysManager).then(hasQuickKeyLayouts => {
      if (hasQuickKeyLayouts) {
        $state.go(STATES.SETTINGS)
      } else {
        quickKeysManager
          .createLayout(quickKeysManager.defaultNewLayoutName)
          .then(
            quickKeyLayout =>
              quickKeysManager.navigateToEditState(quickKeyLayout, true),
            () => {
              setSetupInProgress(false)
            }
          )
      }
    })
  }, [quickKeysManager])

  if (
    trainingModeEnabled ||
    !webRegisterStatusManager.isOnline() ||
    !selectedRegister
  ) {
    return null
  }

  return (
    <div className="wr-quick-keys wr-workspace-module wr-workspace-module--fit-container">
      <div className="wr-workspace-faux-quick-keys">
        <Header type="hero" level="2">
          <T _str="Quick Keys" _context="Heading when no quick keys are set" />
        </Header>
        <div className="wr-workspace-quickkey-splash-content vd-mt6">
          <FauxQuickKeys />
          <div className="vd-p vd-text--secondary vd-mt6">
            <T
              _str="Create custom buttons or Quick Keys for your most popular products,
            and speed up checkouts."
              _context="Message when no quick keys are set up"
            />
            {hasPermission ? null : (
              <span>
                <T
                  _str="Switch to an Admin or Manager to set up your Quick Keys."
                  _context="Message when no quick keys are set up and user has no permission to add them"
                />
              </span>
            )}
          </div>
        </div>
        {setupInProgress ? (
          <div className="wr-workspace-faux-quick-keys">
            <div className="vd-loader vd-mt3"></div>
            <div className="vd-text--secondary vd-mt3">
              <T
                _str="Setting up"
                _context="Message when creating new quick keys layout"
              />
            </div>
            <div className="vd-text--sub vd-text--secondary vd-mt3">
              <T
                _str="This may take a few minutes to complete"
                _context="Message when creating new quick keys layout"
              />
            </div>
          </div>
        ) : (
          <div className="wr-workspace-quickkey-setup-actions vd-mt5">
            {hasPermission ? (
              <Button variant="supplementary" onClick={setUpQuickKeys}>
                <T
                  _str="Set up Quick Keys"
                  _context="Button to set up quick keys"
                />
              </Button>
            ) : (
              <div>
                <Button variant="supplementary" onClick={switchUser}>
                  <T
                    _str="Switch users"
                    _context="Button to switch users if don't have permission to set up quick keys"
                  />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
